import type {
  ChecklistTemplate,
  ChecklistTemplateItem,
  CandidateValue,
  DeviceSheet,
  DeviceSheetScope,
  Evidence,
  AIAdjudication,
  Inference,
  ParameterRecord,
  POCGroup,
  Provenance,
  SourceFile,
  WorkbookTraceIndex,
} from './types'

export const AUDIT_HEADERS = [
  '类别', '参数编号', '参数名称', '参数值（AI建议/采用）', '结果状态', '置信度(%)', '判定依据（候选值与AI判断）', '来源摘要（文件/章节/页码/原文）', '人工填写/复核',
]

export const MANUAL_COLUMN_INDEX = AUDIT_HEADERS.length - 1
export type ManualEdits = Record<string, string>

export interface ManualCellTarget {
  key: string
  recordId?: string
}

export interface RecordFilters {
  text: string
  packageName: string
  site: string
  instanceId: string
  status: string
}

export interface WorkbookBuildResult {
  data: Record<string, unknown>
  rowMaps: WorkbookTraceIndex
  sheetNames: string[]
  editableCells: Record<string, ManualCellTarget>
  deviceSheets: DeviceSheet[]
  sheetByRecordId: Record<string, string>
}

export interface FormalSheetRows {
  sheet: DeviceSheet
  rows: unknown[][]
  rowStyles: string[]
  rowMap: Record<string, string>
  manualKeys: Record<string, ManualCellTarget>
}

const STYLE = {
  title: { fs: 14, bl: 1, cl: '#FFFFFF', bg: { rgb: '#2F5D82' }, ht: 2, vt: 2 },
  header: { fs: 10, bl: 1, cl: '#FFFFFF', bg: { rgb: '#6E96BA' }, ht: 2, vt: 2, tb: 2 },
  body: { fs: 10, vt: 2, tb: 2 },
  'template-label': { fs: 10, bl: 1, bg: { rgb: '#DCEAF7' }, vt: 2, tb: 2 },
  'template-name': { fs: 10, bg: { rgb: '#EAF3FB' }, vt: 2, tb: 2 },
  'status-direct': { fs: 10, bg: { rgb: '#DDF4E4' }, vt: 2, tb: 2 },
  'status-partial': { fs: 10, bg: { rgb: '#FFF2CC' }, vt: 2, tb: 2 },
  'status-inferred': { fs: 10, bg: { rgb: '#DDEBFF' }, vt: 2, tb: 2 },
  'status-missing': { fs: 10, bg: { rgb: '#FCE1E1' }, vt: 2, tb: 2 },
  manual: { fs: 10, bg: { rgb: '#F7F8FA' }, vt: 2, tb: 2 },
  neutral: { fs: 10, bg: { rgb: '#E7E6E6' }, vt: 2, tb: 2 },
}

const CATEGORY_MAP: Record<string, string> = {
  铁心: '铁心部分',
  铁心部分: '铁心部分',
  在线监测: '监测装置',
  监测装置: '监测装置',
  运输及尺寸: '运输及尺寸限制',
  运输及尺寸限制: '运输及尺寸限制',
  绝缘油: '绝缘油类型',
  绝缘油类型: '绝缘油类型',
  非标附件: '招标文件要求的其它非标附件',
  招标文件要求的其它非标附件: '招标文件要求的其它非标附件',
}

function text(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function clipped(value: unknown, limit = 300): string {
  const result = text(value).replace(/\s+/g, ' ').trim()
  if (result.length <= limit) return result
  return `${result.slice(0, Math.max(1, limit - 12)).replace(/[ ;；，,。]+$/u, '')}…`
}

function normalizeName(value: unknown): string {
  return text(value).replace(/\s+/g, '').toLowerCase().replaceAll('（', '(').replaceAll('）', ')')
}

function normalizeNumber(value: unknown): string {
  const result = text(value).trim()
  return result.endsWith('.0') ? result.slice(0, -2) : result
}

function formalCategory(value: unknown): string {
  const result = text(value).trim()
  return CATEGORY_MAP[result] || result
}

export function statusFor(record: ParameterRecord): string {
  if (record.applicability === '不适用') return '不适用'
  if (record.decision_state === '无可用候选') return '未找到'
  if (record.ai_decision_id || (record.conflict_ids || []).length) return '部分找到'
  return text(record.status) || '未找到'
}

export function confidenceFor(record: ParameterRecord | undefined, status = record ? statusFor(record) : '未找到', provenance?: Provenance): string {
  if (status === '不适用') return '—'
  const decision = record && provenance ? decisionFor(record, provenance) : undefined
  const value = Number(decision?.confidence ?? record?.confidence)
  if (!Number.isFinite(value)) return ''
  return `${Math.round((value <= 1 ? value : value / 100) * 100)}%`
}

function unitInValue(value: string, unit: unknown): boolean {
  const normalizedUnit = text(unit).replace(/\s+/g, '').toLowerCase()
  if (!normalizedUnit) return true
  return value.replace(/\s+/g, '').toLowerCase().includes(normalizedUnit)
}

function unitForRecord(record: ParameterRecord): string {
  if (text(record.unit).trim()) return text(record.unit).trim()
  const match = text(record.parameter_name).match(/[（(]([^）)]+)[）)]/u)
  return match?.[1]?.trim() || ''
}

export function valueFor(record: ParameterRecord | undefined, status = record ? statusFor(record) : '未找到'): string {
  if (!record) return '未找到'
  if (status === '不适用') return '不适用'
  if (record.decision_state === '无可用候选') return '未找到'
  const chosen = record.ai_recommended_value ?? record.final_value ?? record.raw_value
  let result = text(chosen).trim()
  if (!result) {
    if (status === '未找到') return '未找到'
    if (status === '部分找到') return '部分找到：候选值待复核'
    if (status === 'AI推理') return 'AI推理值待核验'
    return '—'
  }
  const unit = unitForRecord(record)
  if (!unitInValue(result, unit)) result = `${result} ${unit}`.trim()
  return result
}

function sourceLabel(locator: Record<string, unknown>): string {
  const kind = text(locator.kind).toLowerCase()
  if (kind === 'pdf') {
    const page = locator.page_number ?? locator.pdf_page_number
    return page == null ? 'PDF' : `PDF第${page}页`
  }
  if (kind === 'docx') {
    const page = locator.rendered_page_number ?? locator.page_number
    if (page != null) return `DOCX第${page}页`
    if (locator.table_index != null) return `DOCX表格${Number(locator.table_index) + 1}`
    if (locator.paragraph_index != null) return `DOCX段落${Number(locator.paragraph_index) + 1}`
    return 'DOCX'
  }
  if (kind === 'xlsx') return `XLSX ${text(locator.sheet_name)}!${text(locator.cell_range)}`.replace(/[ !]+$/u, '').trim()
  if (kind === 'pptx') {
    const page = locator.page_number ?? locator.page_index
    return page == null ? 'PPTX' : `PPTX第${page}页`
  }
  return kind.toUpperCase() || '来源'
}

function decisionFor(record: ParameterRecord | undefined, provenance: Provenance): AIAdjudication | undefined {
  if (!record?.ai_decision_id) return undefined
  return provenance.ai_adjudications?.find((item) => item.ai_decision_id === record.ai_decision_id)
}

export function candidateValuesFor(record: ParameterRecord, provenance: Provenance): CandidateValue[] {
  if (record.candidate_values?.length) return record.candidate_values as CandidateValue[]
  const conflicts = (record.conflict_ids || [])
    .map((id) => provenance.conflicts.find((item) => item.conflict_id === id))
    .filter(Boolean)
  return conflicts.flatMap((conflict) => (conflict?.values || []).map((value, index) => ({
    ...value,
    candidate_id: value.candidate_id || `${conflict?.conflict_id || 'candidate'}-${index + 1}`,
    source_file_ids: value.source_file_ids || (value.source_file_id ? [value.source_file_id] : []),
    evidence_ids: value.evidence_ids || [],
    eligibility: value.eligibility || '可选',
  } as CandidateValue)))
}

export function adjudicationFor(record: ParameterRecord | undefined, provenance: Provenance): AIAdjudication | undefined {
  return decisionFor(record, provenance)
}

function chooseEvidence(record: ParameterRecord, evidenceById: Map<string, Evidence>, evidenceIds = record.evidence_ids || []): Evidence | undefined {
  const evidence = evidenceIds
    .map((id) => evidenceById.get(id))
    .filter((item): item is Evidence => Boolean(item))
  return evidence.sort((left, right) => {
    const score = (item: Evidence) => {
      const locator = item.locator || {}
      const quote = item.quote || locator.text_quote?.exact
      const hasLocation = locator.page_number != null || locator.sheet_name || locator.paragraph_index != null || locator.table_index != null
      return Number(Boolean(quote)) * 4 + Number(Boolean(hasLocation)) * 2 + Number(Boolean(locator.regions?.length))
    }
    return score(right) - score(left)
  })[0]
}

function evidenceSummary(evidenceIds: string[], evidenceById: Map<string, Evidence>, sourceById: Map<string, SourceFile>): string[] {
  return [...new Set(evidenceIds)].map((evidenceId) => {
    const evidence = evidenceById.get(evidenceId)
    if (!evidence) return ''
    const locator = evidence.locator || {}
    const source = sourceById.get(evidence.source_file_id)
    const revision = text(evidence.source_version || source?.document_revision)
    const quote = text(evidence.quote || locator.text_quote?.exact || '').replace(/\s+/gu, ' ').trim()
    return [source?.file_name || '来源文件', revision && `版本${revision}`, locator.section, sourceLabel(locator as Record<string, unknown>), quote && `原文：“${quote}”`]
      .filter(Boolean).join(' · ')
  }).filter(Boolean)
}

function evidenceDisplay(record: ParameterRecord, provenance: Provenance): { source: string; basis: string } {
  const evidenceById = new Map(provenance.evidence.map((item) => [item.evidence_id, item]))
  const sourceById = new Map(provenance.source_files.map((item) => [item.source_file_id, item]))
  const decision = decisionFor(record, provenance)
  const candidates = candidateValuesFor(record, provenance)
  const selected = decision?.selected_candidate_id ? candidates.find((item) => item.candidate_id === decision.selected_candidate_id) : undefined
  const allCandidateEvidenceIds = candidates.flatMap((candidate) => candidate.evidence_ids || [])
  const evidenceIds = selected?.evidence_ids?.length ? selected.evidence_ids : record.evidence_ids || allCandidateEvidenceIds
  const evidence = chooseEvidence(record, evidenceById, evidenceIds)
  if (!evidence) return { source: '当前没有可核查证据', basis: '' }
  const sourceIds = allCandidateEvidenceIds.length ? allCandidateEvidenceIds : evidenceIds
  const sourceLines = evidenceSummary(sourceIds, evidenceById, sourceById)
  const sourceText = sourceLines.length > 1 ? sourceLines.map((line, index) => `候选${index + 1}：${line}`).join('\n') : sourceLines[0]
  return { source: sourceText || '来源待复核', basis: record.inference_id ? '推理依据' : decision ? 'AI候选判定' : '原文内容' }
}

function inferenceBasis(record: ParameterRecord, provenance: Provenance): string {
  const status = statusFor(record)
  if (status === '不适用') return '不适用'
  const decision = decisionFor(record, provenance)
  if (decision) return adjudicationBasis(record, provenance, decision)
  const inference = record.inference_id ? provenance.inferences.find((item) => item.inference_id === record.inference_id) : undefined
  if (inference) {
    const pieces = [
      inference.standard && `标准/方法：${inference.standard}`,
      inference.formula && `公式：${inference.formula}`,
      inference.chain?.length && `链条：${inference.chain.join(' → ')}`,
      inference.cross_validation && `交叉验证：${inference.cross_validation}`,
    ].filter(Boolean)
    return clipped(pieces.join('；'), 300)
  }
  if (status === '原文提取') return '原文内容'
  if (status === '部分找到') return '已找到部分信息，候选值或条件不完整，待复核'
  if (status === 'AI推理') return '待复核'
  return ''
}

function candidateLabel(candidate: CandidateValue, index: number): string {
  const value = text(candidate.value).trim() || '空值'
  const unit = candidate.unit ? `｜单位：${candidate.unit}` : ''
  const conditions = candidate.conditions ? `｜条件：${candidate.conditions}` : ''
  const revision = candidate.revision ? `｜版本：${candidate.revision}` : ''
  const scope = [candidate.package, candidate.site, candidate.instance_id].filter(Boolean).join('/')
  const scopeText = scope ? `｜范围：${scope}` : ''
  const state = candidate.eligibility === '排除' ? `｜排除：${candidate.exclusion_reason || '不参与选择'}` : ''
  return `${index + 1}. ${value}${unit}${conditions}${revision}${scopeText}${state}`
}

function adjudicationBasis(record: ParameterRecord, provenance: Provenance, decision: AIAdjudication): string {
  const candidates = candidateValuesFor(record, provenance)
  const selected = decision.selected_candidate_id ? candidates.find((item) => item.candidate_id === decision.selected_candidate_id) : undefined
  const assessmentById = new Map(decision.candidate_assessments.map((item) => [item.candidate_id, item]))
  const candidateLines = candidates.map((candidate, index) => {
    const assessment = assessmentById.get(candidate.candidate_id)
    return `${candidateLabel(candidate, index)}${assessment ? `｜AI：${assessment.result}；${assessment.reason}` : ''}`
  })
  const steps = decision.reasoning_steps.map((step, index) => `${index + 1}. ${step.step}：${step.statement}`).join('\n')
  const uncertainty = decision.uncertainty?.length ? `\n待确认：${decision.uncertainty.join('；')}` : ''
  const conclusion = `AI处理：${decision.decision_state}${selected ? `；建议值：${text(selected.value)}` : ''}`
  return [conclusion, '候选值（全部保留）：', ...candidateLines, '判定过程：', steps, uncertainty].filter(Boolean).join('\n')
}

function isSharedRecord(record: ParameterRecord): boolean {
  return [record.package, record.instance_id, record.site].some((value) => text(value).toUpperCase() === 'SHARED')
    || text(record.instance_id).toUpperCase().includes('SHARED')
}

function isInstallRecord(record: ParameterRecord): boolean {
  return text(record.instance_id).toUpperCase().includes('INSTALL-SCOPE')
}

function isCandidateId(value: unknown): boolean {
  return text(value).toUpperCase().endsWith('-CANDIDATE')
}

function baseCandidateId(value: unknown): string {
  return text(value).replace(/-CANDIDATE$/iu, '')
}

function recordScopeKey(record: ParameterRecord, allRecords: ParameterRecord[]): string {
  if (isSharedRecord(record)) return '__SHARED__'
  if (isInstallRecord(record)) return '__INSTALL__'
  const instance = text(record.instance_id).trim()
  if (!instance) return `${text(record.package)}|${text(record.site)}` || '__UNKNOWN__'
  if (!isCandidateId(instance)) return instance
  const base = baseCandidateId(instance)
  if (allRecords.some((item) => text(item.instance_id) === base)) return base
  const sameScope = [...new Set(allRecords
    .filter((item) => !isCandidateId(item.instance_id) && !isSharedRecord(item) && !isInstallRecord(item)
      && text(item.package) === text(record.package) && text(item.site) === text(record.site) && item.instance_id)
    .map((item) => text(item.instance_id)))]
  return sameScope.length === 1 ? sameScope[0] : '__SHARED__'
}

function displayDeviceName(instanceIds: string[], scope: DeviceSheetScope): string {
  if (scope === 'shared') return '共享项'
  if (scope === 'install-scope') return '安装范围'
  return instanceIds.map((item) => item.replace(/^RW-|^TZ-|^PH-/iu, '').replace(/-CANDIDATE$/iu, '').replace(/-/g, ' ')).join(' / ') || '未指定设备'
}

export function buildDeviceSheets(records: ParameterRecord[]): DeviceSheet[] {
  const groups = new Map<string, { key: string; instanceIds: Set<string>; packages: Set<string>; sites: Set<string>; scope: DeviceSheetScope }>()
  for (const record of records) {
    const key = recordScopeKey(record, records)
    if (!groups.has(key)) groups.set(key, { key, instanceIds: new Set(), packages: new Set(), sites: new Set(), scope: key === '__SHARED__' ? 'shared' : key === '__INSTALL__' ? 'install-scope' : 'transformer' })
    const group = groups.get(key)!
    if (record.instance_id) group.instanceIds.add(text(record.instance_id))
    if (record.package && text(record.package).toUpperCase() !== 'SHARED') group.packages.add(text(record.package))
    if (record.site && text(record.site).toUpperCase() !== 'SHARED') group.sites.add(text(record.site))
  }
  const ordered = [...groups.values()].sort((left, right) => {
    const rank = (item: typeof left) => item.scope === 'transformer' ? 0 : item.scope === 'shared' ? 1 : 2
    return rank(left) - rank(right) || left.key.localeCompare(right.key, 'en', { numeric: true })
  })
  let index = 0
  return ordered.map((group) => {
    const sheetName = group.scope === 'shared' ? '共享项' : group.scope === 'install-scope' ? '安装范围' : `变压器${++index}`
    const instanceIds = [...group.instanceIds]
    return {
      key: group.key,
      sheetName,
      scope: group.scope,
      instanceIds,
      displayName: displayDeviceName(instanceIds, group.scope),
      packages: [...group.packages],
      sites: [...group.sites],
    }
  })
}

function recordMatchScore(record: ParameterRecord, item: ChecklistTemplateItem): number {
  if (formalCategory(record.category) !== item.category) return -1
  const number = normalizeNumber(record.template_number)
  const itemNumber = normalizeNumber(item.parameterNumber)
  const name = normalizeName(record.parameter_name)
  const itemName = normalizeName(item.parameterName)
  let score = 0
  if (name && name === itemName) score += 100
  if (number && itemNumber && number === itemNumber) score += 55
  if (name && itemName && (name.includes(itemName) || itemName.includes(name))) score += 25
  return score
}

function matchedRecords(records: ParameterRecord[], item: ChecklistTemplateItem): ParameterRecord[] {
  return records.map((record) => ({ record, score: recordMatchScore(record, item) }))
    .filter((entry) => entry.score >= 25)
    .sort((left, right) => {
      const candidatePenalty = Number(isCandidateId(left.record.instance_id)) - Number(isCandidateId(right.record.instance_id))
      return right.score - left.score || candidatePenalty || Number(Boolean(right.record.final_value)) - Number(Boolean(left.record.final_value))
        || Number((right.record.evidence_ids || []).length) - Number((left.record.evidence_ids || []).length)
    })
    .map((entry) => entry.record)
}

function basicInfoValue(item: ChecklistTemplateItem, provenance: Provenance, sheet: DeviceSheet): string | undefined {
  if (item.category !== '基本信息') return undefined
  if (item.parameterNumber === '0.1') return text(provenance.project.name)
  if (item.parameterNumber === '0.2') return text(sheet.scope === 'transformer' ? 1 : '')
  if (item.parameterNumber === '0.4') return sheet.displayName
  return undefined
}

function statusStyle(status: string): string {
  if (status === '不适用') return 'neutral'
  if (status === '原文提取') return 'status-direct'
  if (status === '部分找到') return 'status-partial'
  if (status === 'AI推理') return 'status-inferred'
  return 'status-missing'
}

export function manualKeyForRecord(recordId: string): string {
  return `record:${recordId}`
}

export function manualKeyForTemplate(sheetKey: string, matchKey: string): string {
  return `template:${sheetKey}:${matchKey}`
}

export function manualKeyForOverview(field: string): string {
  return `overview:${field}`
}

function rowCell(value: unknown, style: string, trace?: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = { v: text(value), s: style }
  if (trace) result.custom = { trace }
  return result
}

function makeSheet(
  id: string,
  name: string,
  rows: unknown[][],
  rowStyles: string[],
  rowMap: Record<string, string>,
  editableCells: Record<string, ManualCellTarget>,
  template: ChecklistTemplate,
) {
  const cellData: Record<number, Record<number, Record<string, unknown>>> = {}
  rows.forEach((row, rowIndex) => {
    cellData[rowIndex] = {}
    row.forEach((value, columnIndex) => {
      const recordId = rowMap[String(rowIndex)]
      const editableTarget = editableCells[`${name}:${rowIndex}:${columnIndex}`]
      const style = rowIndex === 0 ? 'title' : rowIndex === 1 ? 'header'
        : columnIndex === 0 || columnIndex === 1 ? 'template-label'
          : columnIndex === 2 ? 'template-name'
            : columnIndex === MANUAL_COLUMN_INDEX ? 'manual'
              : (rowStyles[rowIndex] || 'body')
      cellData[rowIndex][columnIndex] = rowCell(value, style, recordId || editableTarget ? {
        trace: {
          record_id: recordId,
          row: rowIndex,
          column: columnIndex,
          column_key: AUDIT_HEADERS[columnIndex],
          editable: Boolean(editableTarget),
          manual_key: editableTarget?.key,
        },
      } : undefined)
    })
  })
  const columnData: Record<number, Record<string, number>> = {}
  const widths = [...template.columnWidths.map((value) => value * 7), 180]
  for (let index = 0; index < 9; index += 1) columnData[index] = { w: widths[index] || 120 }
  const rowData: Record<number, Record<string, number>> = {}
  for (let row = 0; row < rows.length; row += 1) rowData[row] = { h: Number(template.rowHeights[String(row)] || (row === 0 ? 26 : row === 1 ? 48 : 20)) }
  return {
    id,
    name,
    rowCount: rows.length,
    columnCount: 9,
    defaultRowHeight: 20,
    freezeRows: 2,
    freeze: { xSplit: 3, ySplit: 2, startRow: 2, startColumn: 3 },
    mergeData: template.merges.map((merge) => ({
      startRow: merge.startRow,
      startColumn: merge.startColumn,
      endRow: merge.endRow - 1,
      endColumn: merge.endColumn - 1,
    })),
    cellData,
    rowData,
    columnData,
    rowHeader: { width: 36 },
    columnHeader: { height: 20 },
    showGridlines: 1,
  }
}

export function getBusinessRecords(provenance: Provenance, template?: ChecklistTemplate, records: ParameterRecord[] = provenance.parameter_records): ParameterRecord[] {
  if (!template) return records
  return records.filter((record) => template.items.some((item) => recordMatchScore(record, item) >= 25))
}

export function getChecklistDefinitions(provenance: Provenance, template?: ChecklistTemplate) {
  if (template) return template.items.map((item) => ({ parameter_id: `TEMPLATE-${item.templateRow}`, template_number: item.parameterNumber, parameter_name: item.parameterName, category: item.category, source: 'Checklist模版-v1.0.xlsx' }))
  return provenance.parameter_definitions.filter((item) => item.source === 'Checklist sample.xlsx')
}

export function filterRecords(records: ParameterRecord[], filters: RecordFilters): ParameterRecord[] {
  const query = filters.text.trim().toLowerCase()
  return records.filter((record) => {
    if (filters.packageName && record.package !== filters.packageName) return false
    if (filters.site && record.site !== filters.site) return false
    if (filters.instanceId && record.instance_id !== filters.instanceId) return false
    if (filters.status && statusFor(record) !== filters.status) return false
    if (!query) return true
    const decision = record.decision_state || ''
    return [record.parameter_name, record.parameter_id, record.template_number, record.raw_value, record.final_value, record.ai_recommended_value, record.conditions, record.record_id, decision, record.candidate_values?.map((candidate) => candidate.value).join(' '), record.notes]
      .some((value) => text(value).toLowerCase().includes(query))
  })
}

export function getCategoryNames(provenance: Provenance): string[] {
  return [...new Set(provenance.parameter_definitions.map((item) => item.category).filter(Boolean))]
}

export function getPocGroupForSheet(groups: POCGroup[], sheet: string): POCGroup | undefined {
  return groups.find((group) => group.sheets.includes(sheet))
}

export function businessRow(provenance: Provenance, record: ParameterRecord, manualValue = ''): unknown[] {
  const evidence = evidenceDisplay(record, provenance)
  return ['', normalizeNumber(record.template_number) || '—', text(record.parameter_name) || '—', valueFor(record), statusFor(record), confidenceFor(record, undefined, provenance), inferenceBasis(record, provenance), evidence.source, manualValue]
}

export function buildFormalSheetRows(
  provenance: Provenance,
  template: ChecklistTemplate,
  sheet: DeviceSheet,
  visibleRecords: ParameterRecord[],
  allRecords: ParameterRecord[],
  manualEdits: ManualEdits = {},
): FormalSheetRows {
  const rows: unknown[][] = [['变压器点检表', '', '', '', '', '', '', '', ''], template.headers]
  const rowStyles: string[] = []
  const rowMap: Record<string, string> = {}
  const manualKeys: Record<string, ManualCellTarget> = {}
  const sheetRecords = visibleRecords.filter((record) => recordScopeKey(record, allRecords) === sheet.key)
  const allSheetRecords = allRecords.filter((record) => recordScopeKey(record, allRecords) === sheet.key)
  template.items.forEach((item, itemIndex) => {
    const rowIndex = itemIndex + 2
    const matched = matchedRecords(sheetRecords, item)
    const allMatched = matchedRecords(allSheetRecords, item)
    const record = matched[0]
    const filteredOut = !record && Boolean(allMatched[0])
    const basicValue = basicInfoValue(item, provenance, sheet)
    const status = filteredOut ? '' : record ? statusFor(record) : basicValue !== undefined ? '原文提取' : '未找到'
    const evidence = filteredOut ? { source: '', basis: '' } : record ? evidenceDisplay(record, provenance) : { source: basicValue !== undefined ? '项目元数据' : '当前没有可核查证据', basis: basicValue !== undefined ? '项目元数据' : '' }
    const value = filteredOut ? '' : record ? valueFor(record, status) : basicValue !== undefined ? basicValue : '未找到'
    const row = [
      itemIndex === 0 || template.items[itemIndex - 1]?.category !== item.category ? item.category : '',
      item.parameterNumber,
      item.parameterName,
      value,
      status,
      record ? confidenceFor(record, status, provenance) : basicValue !== undefined ? '100%' : '',
      record ? inferenceBasis(record, provenance) : evidence.basis,
      evidence.source,
      '',
    ]
    const manualKey = record ? manualKeyForRecord(record.record_id) : manualKeyForTemplate(sheet.key, item.matchKey)
    row[MANUAL_COLUMN_INDEX] = manualEdits[manualKey] || ''
    rows.push(row)
    rowStyles[rowIndex] = status ? statusStyle(status) : 'body'
    if (record) rowMap[String(rowIndex)] = record.record_id
    manualKeys[`${sheet.sheetName}:${rowIndex}:${MANUAL_COLUMN_INDEX}`] = { key: manualKey, recordId: record?.record_id }
  })
  // Keep the calculated collection reachable for diagnostics without changing
  // the visible table. This makes it explicit that duplicate candidates stay
  // in provenance even when one business value is selected for display.
  void allSheetRecords
  return { sheet, rows, rowStyles, rowMap, manualKeys }
}

export function buildAuditWorkbook(
  provenance: Provenance,
  visibleRecords: ParameterRecord[],
  manualEdits: ManualEdits = {},
  template: ChecklistTemplate,
  allRecords: ParameterRecord[] = provenance.parameter_records,
): WorkbookBuildResult {
  const formalAllRecords = getBusinessRecords(provenance, template, allRecords)
  const formalVisibleRecords = getBusinessRecords(provenance, template, visibleRecords)
  const deviceSheets = buildDeviceSheets(formalAllRecords)
  const sheets: Record<string, unknown> = {}
  const sheetOrder: string[] = []
  const rowMaps: WorkbookTraceIndex = {}
  const editableCells: Record<string, ManualCellTarget> = {}
  const sheetByRecordId: Record<string, string> = {}

  deviceSheets.forEach((sheet, index) => {
    const built = buildFormalSheetRows(provenance, template, sheet, formalVisibleRecords, formalAllRecords, manualEdits)
    const id = `sheet-${index + 1}`
    sheetOrder.push(id)
    sheets[id] = makeSheet(id, sheet.sheetName, built.rows, built.rowStyles, built.rowMap, built.manualKeys, template)
    rowMaps[sheet.sheetName] = built.rowMap
    Object.assign(editableCells, built.manualKeys)
    Object.values(built.rowMap).forEach((recordId) => { sheetByRecordId[recordId] = sheet.sheetName })
  })

  return {
    data: {
      id: `workbook-${provenance.project.project_key}`,
      name: provenance.project.name,
      appVersion: '0.25.1',
      locale: 'zh_CN',
      styles: STYLE,
      sheetOrder,
      sheets,
      custom: { template_version: template.version, device_sheets: deviceSheets },
    },
    rowMaps,
    sheetNames: deviceSheets.map((sheet) => sheet.sheetName),
    editableCells,
    deviceSheets,
    sheetByRecordId,
  }
}
